import {
  GetLocationsTreeQueryResult,
  IntegrationType,
  LocationAssociationEntityType,
  useGetLocationsTreeLazyQuery,
} from '@kinetic-ui/data-layer';
import { useCallback } from 'react';
import { DataSource, TreeSelectOption } from '@qfa/forms';
import { getNodes, isNonNull, isValidIntersectionLocation } from 'kineticUtils';
import { locationsTreeBuilder } from 'kineticUtils/locations/locationsTreeBuilder';
import { MapValue } from 'types/util';
import { useTranslation } from 'react-i18next';
import { FieldEntityProtocol } from 'types/fieldEntityProtocol';

export const LOCATION_TREE_ICONS = {
  signal: 'e5d1',
  locationGroup: 'e886',
};

export type CenterIdsQueryOptions = 'all' | 'none' | string[];

type TreeLocation = NonNullable<
  GetLocationsTreeQueryResult['data']
>['locations']['edges'][number]['node'];

type Location = NonNullable<
  MapValue<ReturnType<typeof locationsTreeBuilder<any, TreeLocation>>['locationMap']>
>;

export type ToLocationTreeOption = (location: Location) => TreeSelectOption | null;

export type UseIntersectionLocationTreeDataSourceOptions = {
  centerIds: CenterIdsQueryOptions;
  toLocationOption: ToLocationTreeOption;
  toLocationGroupOption?: typeof defaultToLocationGroupOption;
  groupsDisabled: boolean | ((groupId: string) => boolean);
  showDisabledSignals?: boolean;
};

export const UNGROUPED_ID = 'UNGROUPED';

export enum IntersectionLocationTreeTypes {
  LocationGroup = 'locationGroup',
  Signal = 'signal',
}

const isC2C = (item: Object): item is { externalCenterId: string } => {
  return 'externalCenterId' in item && !!item.externalCenterId;
};

/** Creates an option where the location's signal entity ID is the value */
export const toSignalLocationOption: ToLocationTreeOption = (location) => {
  // we can use non-nullable here because isValidIntersectionLocation checks for this.
  const signal = location.associatedEntities.find(
    (e) => e.entityType === LocationAssociationEntityType.SignalsIntersection
  )!;

  if (!signal) {
    return null;
  }

  return {
    id: signal.entityId,
    label: location.details.name,
    value: signal.entityId,
    type: IntersectionLocationTreeTypes.Signal,
    disabled: false,
    protocol: signal.protocol,
  };
};

export const toSignalLocationOptionByProtocols = (
  location: Location,
  protocols: FieldEntityProtocol[]
) => {
  const signalLocation = toSignalLocationOption(location);
  if (
    !protocols.includes(
      location?.associatedEntities?.find(
        (e) => e.entityType === LocationAssociationEntityType.SignalsIntersection
      )?.protocol as FieldEntityProtocol
    )
  ) {
    return null;
  }
  return signalLocation;
};

const MaxtimeProtocols = [FieldEntityProtocol.MAXTIME_1, FieldEntityProtocol.MAXTIME_2];

export const toSignalMaxtimeLocationOption: ToLocationTreeOption = (location) =>
  toSignalLocationOptionByProtocols(location, MaxtimeProtocols);

export const toSynchroExportLocation: ToLocationTreeOption = (location) => {
  // we can use non-nullable here because isValidIntersectionLocation checks for this.
  const signal = location.associatedEntities.find(
    (e) => e.entityType === LocationAssociationEntityType.SignalsIntersection
  )!;

  return {
    id: signal.entityId,
    label: location.details.name,
    value: signal.entityId,
    type: IntersectionLocationTreeTypes.Signal,
    disabled: false,
  };
};

/** Creates an option where the location's signal's integration ID is the value */
export const toSignalIntegrationLocationOption: ToLocationTreeOption = (location) => {
  // we can use non-nullable here because isValidIntersectionLocation checks for this.
  const signalIntegration = location.associatedEntities.find(
    (i) => i.systemEnabled && i.integrationType === IntegrationType.Signal
  )!;

  return {
    id: signalIntegration.entityId,
    label: location.details.name,
    value: signalIntegration.entityId,
    type: IntersectionLocationTreeTypes.Signal,
    disabled: false,
  };
};

type MappedGroup = Exclude<
  GetLocationsTreeQueryResult['data'],
  null | undefined
>['locationGroups'][number];

const defaultToLocationGroupOption = (group: MappedGroup): TreeSelectOption | null => {
  return {
    id: group.id,
    label: group.name,
    value: group.id,
    type: IntersectionLocationTreeTypes.LocationGroup,
  };
};

export const useIntersectionsLocationTreeDataSource = ({
  centerIds,
  toLocationOption,
  groupsDisabled,
  showDisabledSignals = false,
  toLocationGroupOption = defaultToLocationGroupOption,
}: UseIntersectionLocationTreeDataSourceOptions): DataSource<TreeSelectOption[]> => {
  const [getTreeData] = useGetLocationsTreeLazyQuery();
  const { t } = useTranslation('locations');

  const load = useCallback(async () => {
    const { data } = await getTreeData();

    const {
      groupMap,
      locationMap,
      orderedGroups: groups,
      orderedLocations: locations,
    } = locationsTreeBuilder({
      groups: data?.locationGroups ?? [],
      locations: getNodes(data?.locations),
    });

    const makeLocationOption = (locationId: string): TreeSelectOption | null => {
      const location = locationMap.get(locationId)!;

      if (!isValidIntersectionLocation(location, showDisabledSignals)) {
        return null;
      }
      return toLocationOption(location);
    };

    const makeGroupOption = (groupId: string): TreeSelectOption | null => {
      const group = groupMap.get(groupId)!;
      const childGroupIds = Array.from(group.childGroups);
      const childLocationIds = Array.from(group.childLocations);
      const isC2CGroup = isC2C(group);

      // We want to filter to only the local center
      if (centerIds === 'none' && isC2CGroup) {
        return null;
      }

      if (
        Array.isArray(centerIds) &&
        (!isC2CGroup || !centerIds.includes(group.externalCenterId))
      ) {
        return null;
      }
      const groupOption = toLocationGroupOption(group);
      if (!groupOption) {
        return null;
      }

      const children = childGroupIds
        .map(makeGroupOption)
        .concat(childLocationIds.map(makeLocationOption))
        .filter(isNonNull);

      const disabled =
        typeof groupsDisabled === 'function' ? groupsDisabled(group.id) : groupsDisabled;

      const finalOption = {
        type: IntersectionLocationTreeTypes.LocationGroup,
        disabled,
        ...groupOption,
        children,
      };

      if (finalOption.children.length === 0 && finalOption.disabled) {
        // if there are no children, and the group itself is disabled, we won't show it
        return null;
      }

      return finalOption;
    };

    const options: TreeSelectOption[] = [];
    groups.forEach((group) => {
      if (group.parentId) {
        return;
      }
      const groupOption = makeGroupOption(group.id);

      if (groupOption) {
        options.push(groupOption);
      }
    });

    const ungroupedGroup: TreeSelectOption = {
      id: UNGROUPED_ID,
      label: t('ungroupedLocations'),
      disabled:
        typeof groupsDisabled === 'function' ? groupsDisabled(UNGROUPED_ID) : groupsDisabled,
      value: UNGROUPED_ID,
      type: IntersectionLocationTreeTypes.LocationGroup,
      children: locations
        .filter((l) => !l.groupId)
        .map((l) => makeLocationOption(l.id))
        .filter(isNonNull),
    };

    const allUngroupedAreDisabled = ungroupedGroup.children!.every((option) => option.disabled);

    if (
      (!ungroupedGroup.disabled || ungroupedGroup.children!.length !== 0) &&
      !allUngroupedAreDisabled
    ) {
      options.push(ungroupedGroup);
    }

    return options.filter(isNonNull);
  }, [
    centerIds,
    getTreeData,
    groupsDisabled,
    showDisabledSignals,
    t,
    toLocationGroupOption,
    toLocationOption,
  ]);

  return { load };
};
